import { useContext } from 'react';

import { useResizeObserver } from '../../../core/hooks/useResizeObserver.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import { OverlayProvider } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { useAriaLabelingContext } from '../../core/context/AriaLabelingContext.js';
import {
  COLUMN_HEADER_HEIGHT,
  MAX_SIDE_LABEL_WIDTH_RATIO,
  SANKEY_CHART_ARIA_LABEL,
} from '../constants.js';
import { SankeyColumnLabelsContext } from '../context/SankeyColumnLabels.context.js';
import { useNodeColors } from '../hooks/use-node-colors.js';
import { SankeyLayoutProvider } from '../providers/SankeyLayoutProvider.js';
import { SankeyStoreProvider } from '../providers/SankeyStoreProvider.js';
import { graphAreaWrapper } from '../SankeyChart.sty.js';
import { SankeyGraphAreaContent } from './SankeyGraphAreaContent.js';

export function SankeyGraphArea() {
  const { height } = useGraphSize();
  const nodeColors = useNodeColors();
  const columnLabels = useContext(SankeyColumnLabelsContext);
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = useAriaLabelingContext();

  const { ref: outerRef, width: outerWidth } =
    useResizeObserver<HTMLDivElement>();
  const { ref: canvasRef, width: measuredWidth } =
    useResizeObserver<HTMLDivElement>();
  const canvasWidth = measuredWidth ?? 0;

  const maxSideLabelWidth =
    outerWidth !== undefined && outerWidth > 0
      ? Math.floor(outerWidth * MAX_SIDE_LABEL_WIDTH_RATIO)
      : undefined;

  const layoutHeight = columnLabels
    ? Math.max(0, height - COLUMN_HEADER_HEIGHT)
    : height;

  return (
    <div
      className={graphAreaWrapper}
      role="img"
      aria-label={ariaLabel ?? SANKEY_CHART_ARIA_LABEL}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-details={ariaDetails}
    >
      <SankeyStoreProvider>
        <SankeyLayoutProvider width={canvasWidth} height={layoutHeight}>
          <OverlayProvider>
            <TooltipProvider persistedStore>
              <SankeyGraphAreaContent
                outerRef={outerRef}
                canvasRef={canvasRef}
                canvasWidth={canvasWidth}
                outerWidth={outerWidth ?? 0}
                height={layoutHeight}
                maxSideLabelWidth={maxSideLabelWidth}
                nodeColors={nodeColors}
              />
            </TooltipProvider>
          </OverlayProvider>
        </SankeyLayoutProvider>
      </SankeyStoreProvider>
    </div>
  );
}

SankeyGraphArea['displayName'] = 'SankeyGraphArea';
